import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, Modal, FlatList, StyleSheet, ActivityIndicator, TextInput } from 'react-native';
import APIService from '../services/api';

interface Cliente {
  id: number;
  nombre: string;
  telefono: string;
  nit?: string;
}

interface ClienteSelectorProps {
  selectedCliente?: Cliente | null;
  onClienteChange: (cliente: Cliente | null) => void;
}

const ClienteSelector: React.FC<ClienteSelectorProps> = ({
  selectedCliente,
  onClienteChange,
}) => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [filteredClientes, setFilteredClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Cargar clientes al montar el componente
  useEffect(() => {
    loadClientes();
  }, []);

  // Filtrar clientes cuando cambia el texto de búsqueda
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredClientes(clientes);
    } else {
      const filtered = clientes.filter(cliente => 
        cliente.nombre.toLowerCase().includes(searchText.toLowerCase()) ||
        cliente.telefono.includes(searchText) ||
        (cliente.nit && cliente.nit.includes(searchText))
      );
      setFilteredClientes(filtered);
    }
  }, [searchText, clientes]);

  const loadClientes = async () => {
    setLoading(true);
    try {
      const data = await APIService.getClientes();
      console.log('👥 ClienteSelector: Clientes cargados:', data.length);
      setClientes(data);
      setFilteredClientes(data);
    } catch (error) {
      console.error('❌ Error cargando clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCliente = (cliente: Cliente) => {
    onClienteChange(cliente);
    setShowPicker(false);
    setSearchText('');
  };

  const handleClear = () => {
    onClienteChange(null);
  };

  return (
    <View>
      {/* Selector de Cliente */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Referido por (opcional)</Text>
        <TouchableOpacity 
          style={styles.selector}
          onPress={() => setShowPicker(true)}
        >
          <Text style={selectedCliente ? styles.selectedText : styles.placeholderText}>
            {selectedCliente ? `${selectedCliente.nombre} (${selectedCliente.telefono})` : 'Selecciona un cliente'}
          </Text>
          <Text style={styles.arrow}>▼</Text>
        </TouchableOpacity>
        
        {selectedCliente && (
          <TouchableOpacity onPress={handleClear} style={styles.clearButton}>
            <Text style={styles.clearButtonText}>Limpiar selección</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Modal de Clientes */}
      <Modal
        visible={showPicker}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowPicker(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Selecciona un Cliente</Text>
              <TouchableOpacity onPress={() => {
                setShowPicker(false);
                setSearchText('');
              }}>
                <Text style={styles.closeButton}>✕</Text>
              </TouchableOpacity>
            </View>

            {/* Buscador */}
            <View style={styles.searchContainer}>
              <TextInput
                style={styles.searchInput}
                placeholder="Buscar por nombre, teléfono o NIT..."
                value={searchText}
                onChangeText={setSearchText}
                autoCapitalize="none"
              />
            </View>

            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#E74C3C" />
                <Text style={styles.loadingText}>Cargando clientes...</Text>
              </View>
            ) : (
              <FlatList
                data={filteredClientes}
                keyExtractor={(item) => item.id.toString()}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={[
                      styles.listItem,
                      selectedCliente?.id === item.id && styles.listItemSelected
                    ]}
                    onPress={() => handleSelectCliente(item)}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={styles.clienteNombre}>{item.nombre}</Text>
                      <Text style={styles.clienteTelefono}>
                        📱 {item.telefono}
                      </Text>
                      {item.nit && (
                        <Text style={styles.clienteNit}>
                          🆔 NIT: {item.nit}
                        </Text>
                      )}
                    </View>
                    {selectedCliente?.id === item.id && (
                      <Text style={styles.checkmark}>✓</Text>
                    )}
                  </TouchableOpacity>
                )}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>
                      {searchText ? 'No se encontraron clientes' : 'No hay clientes disponibles'}
                    </Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: 8,
  },
  selector: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    borderWidth: 1,
    borderColor: '#DFE6E9',
    borderRadius: 8,
    padding: 14,
  },
  selectedText: {
    fontSize: 14,
    color: '#2C3E50',
    flex: 1,
  },
  placeholderText: {
    fontSize: 14,
    color: '#95A5A6',
    flex: 1,
  },
  arrow: {
    fontSize: 12,
    color: '#95A5A6',
    marginLeft: 8,
  },
  clearButton: {
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  clearButtonText: {
    fontSize: 12,
    color: '#E74C3C',
    textDecorationLine: 'underline',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    paddingBottom: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#DFE6E9',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2C3E50',
  },
  closeButton: {
    fontSize: 24,
    color: '#95A5A6',
    fontWeight: 'bold',
  },
  searchContainer: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#DFE6E9',
  },
  searchInput: {
    backgroundColor: '#F8F9FA',
    borderWidth: 1,
    borderColor: '#DFE6E9',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#95A5A6',
  },
  listItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F8F9FA',
  },
  listItemSelected: {
    backgroundColor: '#FFF5F5',
  },
  clienteNombre: {
    fontSize: 16,
    color: '#2C3E50',
    fontWeight: '500',
    marginBottom: 4,
  },
  clienteTelefono: {
    fontSize: 14,
    color: '#95A5A6',
  },
  clienteNit: {
    fontSize: 13,
    color: '#7F8C8D',
    marginTop: 2,
  },
  checkmark: {
    fontSize: 20,
    color: '#E74C3C',
    fontWeight: 'bold',
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#95A5A6',
    textAlign: 'center',
  },
});

export default ClienteSelector;
